import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import priceQuotes from 'src/repositories/priceQuotes';
import { MACHINES_TO_SORT_SIZE } from 'src/constants/app';
import {
  getSizes,
  removeZeroAndDuplicateItem,
  sortSizesByValue,
} from 'src/utils/helper';
import useQueryUrl from 'src/hooks/useQueryUrl';
import { v4 as uuidv4 } from 'uuid';
import { createFileFromString } from 'src/utils/helper';
import { I18n } from 'src/libs/i18n';
import _ from 'lodash';
import LocalStorage from 'src/utils/LocalStorage';

const apiPath = '/quotations';
const apiDownloadFileCsv = '/quotations/csv-template';
const apiuploadFileCsv = '/quotations/csv-import';
const pdfPath = '/order-histories/pdf';

type UseQuotation = {
  setErrors?: (errors: string[]) => void;
};

export const usePriceQuotes = ({ setErrors }: UseQuotation) => {
  const params = useQueryUrl<SearchParams.MultipleQuotesSearch>();

  const downloadFileCsvPriceQuotes = useMutation<
    AxiosResponse<any>,
    AxiosError<Response.Error>
  >({
    mutationKey: [apiDownloadFileCsv],
    mutationFn: () => {
      return priceQuotes.downloadSingleCsv(
        apiDownloadFileCsv,
        LocalStorage.lang,
      );
    },
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(new Blob([data.data]));
      const link = document.createElement('a');
      const filename = data?.headers['content-disposition'].split('=')[1];
      link.href = url;
      link.setAttribute('download', `${filename}`);
      document.body.appendChild(link);
      link.click();
    },
  });

  const uploadFileCsvPriceQuotes = useMutation<
    AxiosResponse<any>,
    AxiosError<any>,
    Payload.UploadFileCsvPriceQuotes
  >({
    mutationKey: [apiuploadFileCsv],
    mutationFn: (args) =>
      priceQuotes.uploadFileCsv(apiuploadFileCsv, args, LocalStorage.lang),
  });

  const estimatePrices = useMutation<any, AxiosError<Response.Error>, any>({
    mutationKey: [apiPath],
    mutationFn: (args) =>
      priceQuotes.postEstimatePrices(apiPath, args, LocalStorage.lang),
    onSuccess: () => {},
  });

  const queryQuotation = useQuery<any, AxiosError<Response.Error>>({
    queryKey: [
      apiPath,
      { quotationNos: params?.webMitrNos },
      LocalStorage.lang,
    ],
    queryFn: priceQuotes.getQuotations,
    retry: 0,
    enabled: !!params?.webMitrNos,
  });

  const quotations = queryQuotation?.data?.data?.quotations.map((item) => {
    let sizes = getSizes(
      removeZeroAndDuplicateItem(item?.sizeRes?.sizes),
      true,
    );

    if (MACHINES_TO_SORT_SIZE.includes(item?.setdnKakoCd)) {
      sizes = sortSizesByValue(sizes);
    }
    return {
      ...item,
      id: uuidv4(),
      product: item?.productCodes,
      isCreate: false,
      sizeRes: {
        ...item?.sizeRes,
        sizes,
      },
    };
  });

  const exportPDF = useMutation<
    AxiosResponse<any>,
    AxiosError<Response.Error>,
    Payload.ExportPDF
  >({
    mutationKey: [pdfPath],
    mutationFn: (args) => {
      return priceQuotes.exportPDF(pdfPath, args);
    },
    onSuccess: (data) => {
      createFileFromString(data.data, 'Quotation.pdf');
    },
    onError: async (err) => {
      const error = _.get(err, 'response.data');
      const text = await error.text();
      const errorData = JSON.parse(text);
      const errorCode = errorData?.code;
      if (errorCode) {
        setErrors?.([I18n.t(`message.${errorCode}`)]);
      }
    },
  });

  return {
    quotations,
    accountInfo: queryQuotation?.data?.data?.rmOtokesaki,
    errorsWhenReQuoting: queryQuotation?.data?.data?.msgCodes,
    estimatePrices,
    downloadFileCsvPriceQuotes,
    uploadFileCsvPriceQuotes,
    exportPDF,
    isLoading: estimatePrices?.isLoading || queryQuotation?.isFetching,
  };
};
